import { postPlayerSignUp } from '@/api/sign-up/postPlayerSignUp';
import { PostPlayerSignUpRequest } from '@/api/type/types';
import { countries } from '@/constants/countries';
import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import MemberInfoSection from '../all/MemberInfoSection';
import LoginInfoSection from '../all/LoginInfoSection';
import PersonalInfoSection from '../all/BasicPersonalInfoSection';
import CountrySelectField from '../all/CountrySelectField';
import PlayerPhoneNumberFiled from './PlayerPhoneNumberFiled';
import AddressSection from '../all/AddressSection';
import { validatePassword } from '@/utils/regex';
import { fonts } from '@/theme/fonts';
import { useTranslation } from 'react-i18next';

const PlayerSignUpPage = () => {
  const [formData, setFormData] = useState<PostPlayerSignUpRequest>({
    loginId: '',
    password: '',
    firstName: '',
    lastName: '',
    birthDate: '',
    gender: 'MALE',
    countryCode: 'KR',
    contactCountryNumber: '',
    contactNumber: '',
    agreementId: 'AGR20240818',
    agreementVersion: '1.0',
    clinicName: '',
    clinicContactNumber: '',
    clinicAddress: {
      postalCode: '',
      address1: '',
      address2: '',
    },
    remark: '',
  });
  const [passwordConfirm, setPasswordConfirm] = useState<string>('');
  const [passwordError, setPasswordError] = useState<string>('');
  const { t } = useTranslation();

  // 국적 변경 시 국가코드 자동 매칭 로직
  useEffect(() => {
    const selectedCountry = countries.find(
      (country) => country.code === formData.countryCode
    );

    const phoneCode = selectedCountry?.phone ?? '';

    setFormData((prev) => ({
      ...prev,
      contactCountryNumber: phoneCode,
    }));
  }, [formData.countryCode]);

  const handleChange = useCallback(
    (
      e: React.ChangeEvent<
        HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
      >
    ) => {
      const { name, value, type } = e.target;

      if (type === 'checkbox') {
        const checked = (e.target as HTMLInputElement).checked;

        setFormData((prev) => ({
          ...prev,
          [name]: checked,
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          [name]: value,
        }));
      }
    },
    []
  );

  const handleAddressChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      setFormData((prev) => ({
        ...prev,
        clinicAddress: {
          ...prev.clinicAddress,
          [name]: value,
        },
      }));
    },
    []
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (!validatePassword(formData.password)) {
        alert(t('signUp.passwordRule'));
        return;
      }
      if (formData.password !== passwordConfirm) {
        alert(t('signUp.passwordMismatch'));
        return;
      }

      try {
        const res = await postPlayerSignUp(formData);
        if (res.isSuccess) {
          alert(
            t('playerSignUp.registerSuccess', {
              name: res.data.loginId,
            })
          );
        } else if (res.error.code === 'E_MEMBERSHIP_001') {
          alert(t('signUp.emailAlreadyExists'));
        }
      } catch (err) {
        alert(t('common.serverConnectionFailed'));
      }
    },
    [formData, passwordConfirm, t]
  );

  const selectedCountry = countries.find(
    (country) => country.code === formData.countryCode
  );

  const phoneCode = selectedCountry ? `+${selectedCountry.phone}` : '';

  return (
    <Container>
      <MemberInfoSection role="PLAYER" />
      <form onSubmit={handleSubmit}>
        <LoginInfoSection
          loginId={formData.loginId}
          password={formData.password}
          passwordConfirm={passwordConfirm}
          passwordError={passwordError}
          onChange={handleChange}
          onPasswordConfirmChange={(value) => {
            setPasswordConfirm(value);

            if (formData.password && formData.password !== value) {
              setPasswordError(t('signUp.passwordMismatch'));
            } else {
              setPasswordError('');
            }
          }}
        />
        <PersonalInfoSection formData={formData} onChange={handleChange} />
        <Section>
          <SectionTitle>{t('playerSignUp.contactAndAffiliation')}</SectionTitle>
          <GridContainer>
            <CountrySelectField
              countryCode={formData.countryCode}
              onChange={handleChange}
            />
            <PlayerPhoneNumberFiled
              formData={formData}
              phoneCode={phoneCode}
              onChange={handleChange}
            />
            <FormGroup>
              <Label $required>{t('playerSignUp.officeName')}</Label>
              <Input
                type="text"
                name="clinicName"
                value={formData.clinicName}
                onChange={handleChange}
                placeholder={t('playerSignUp.officeNamePlaceholder')}
                required
              />
            </FormGroup>
            <AddressSection
              countryCode={formData.countryCode}
              address={formData.clinicAddress}
              onChange={handleAddressChange}
              role="PLAYER"
            />
            <FormGroup>
              <Label $required>{t('signUp.registrationReason')}</Label>
              <TextArea
                name="remark"
                value={formData.remark}
                onChange={handleChange}
                placeholder={t('signUp.registrationReasonPlaceholder')}
                rows={4}
              />
            </FormGroup>
          </GridContainer>
        </Section>
        <ButtonSection>
          <Button type="submit">{t('playerSignUp.createPlayerAccount')}</Button>
        </ButtonSection>
      </form>
    </Container>
  );
};

export default PlayerSignUpPage;

const Container = styled.div`
  width: 100%;
  max-width: 1000px;
  padding: 24px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
`;

const Section = styled.section`
  padding: 20px 0;
  border-bottom: 1px solid #e5e7eb;
`;

const SectionTitle = styled.h3`
  margin-bottom: 16px;
  font-size: 15px;
  font-weight: ${fonts.weight.semiBold};
  color: #374151;
  margin-bottom: 16px;
`;

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;

interface LabelProps {
  $required?: boolean;
}

const Label = styled.label<LabelProps>`
  font-size: 13px;
  font-weight: 600;
  color: #4a4a4a;

  &::after {
    content: ${(props) => (props.$required ? "' *'" : "''")};
    color: #ff4d4f;
  }
`;

interface InputProps {
  $error?: boolean;
}

const Input = styled.input<InputProps>`
  height: 38px;
  padding: 0 12px;
  font-size: 14px;
  color: #374151;
  border: 1px solid ${({ $error }) => ($error ? '#ef4444' : '#d1d5db')};
  border-radius: 6px;
  outline: none;
  &:focus {
    border-color: #2563eb;
  }
  &::placeholder {
    color: #9ca3af;
  }
`;

const ButtonSection = styled.div`
  display: flex;
  justify-content: end;
  padding-top: 24px;
`;

const Button = styled.button`
  padding: 0 15px;
  height: 40px;
  border-radius: 6px;
  background: #2563eb;
  color: #ffffff;
  font-size: 14px;
  font-weight: ${fonts.weight.semiBold};
  &:hover {
    background: #1d4ed8;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 10px 14px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  line-height: 1.5;
  resize: vertical;
  box-sizing: border-box;
  outline: none;
  resize: none;

  &:focus {
    border-color: #2563eb;
  }

  &::placeholder {
    color: #bfbfbf;
  }
`;
